/**
 * The matter-action footer on the case detail panel — the operator's write
 * surface. Renders the actions valid from the placement's current decision
 * AND the lead-level actions valid from the lead's current status:
 *
 *   - **Accept** when the firm has not yet decided (Sent / Reviewing). One-click (reversible via the portal's still-reviewing).
 *   - **Decline** when the firm can decline (the band + Assigned + Accepted).
 *     Requires a reason, mirroring the portal.
 *   - **Pull** when the firm can be removed (CC-native, same gate as Decline).
 *     Bodyless confirm — pull carries no firm rationale, only the audit row.
 *   - **Close Case** when the placement is Placed or Settled and not yet
 *     closed. Bodyless confirm. Stamps Case Closed Date — funnel-terminal,
 *     not a band move. Gate tightens the portal's allow-any (ADR-0070).
 *   - **Confirm LF Payment** (5b) when Settled AND `lfPaymentDate === null`.
 *     Bodyless confirm — stamps LF Payment Date + Last Update.
 *   - **Confirm OF Payment** (5b) when Settled AND LF set AND OF unset.
 *     Bodyless confirm — stamps OF Payment Date only (no Last Update,
 *     portal asymmetry).
 *   - **Edit Settlement** (5b) when Settled. Opens a small form prefilled
 *     with the three settlement fields; submits only the diff.
 *   - **Reject Lead** (5c) when the lead's status is not terminal
 *     (`canReject`). Requires a reason. Cascades placement declines
 *     server-side before flipping Lead.Status — ADR-0064 inheritance keeps
 *     the cascade strand-free.
 *   - **Edit Client** (5c) — always available when a lead is in scope.
 *     Opens a form that lazily fetches the live lead via `fetchLead` for
 *     prefill (avoids piping 13 fields through every book composer);
 *     submits only the diff.
 *
 * Button visibility uses the SAME gates the route enforces server-side, so the
 * UI never offers an action the server will reject. Decline / Pull / Close /
 * Confirm LF / Confirm OF / Reject each take a confirm step because each is
 * terminal (or near-terminal — payment confirms are idempotent server-side
 * but worth a click of friction). Edit Settlement + Edit Client open forms
 * because they carry input. Accept stays one-click — it's reversible via
 * still-reviewing.
 *
 * On success the panel closes and the affected books refresh (see
 * usePlacementAction).
 *
 * Extracted from `case-detail-panel.tsx` in slice 5a; extended in 5b + 5c.
 */

import { useEffect, useState } from "react";
import {
  ACCEPT_GATE,
  ATTORNEY_FEE_RATE_MAX,
  canClose,
  canConfirmLfPayment,
  canConfirmOfPayment,
  canEditSettlement,
  canReject,
  canReroute,
  canRoute,
  DECLINE_GATE,
  LEAD_CASE_TYPES,
  PULL_GATE,
  US_STATE_CODES,
} from "@command-center/shared";
import type { EligibleFirm, Lead } from "@command-center/shared";
import type {
  CaseDetailActionTarget,
  CaseDetailLeadActionTarget,
} from "@/lib/case-detail";
import { usePlacementAction } from "@/lib/use-placement-action";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/** The footer's mode — exactly one panel-state at a time. Keeps the
 *  conditional chain exhaustive as the action set grows. */
type FooterMode =
  | "buttons"
  | "decline"
  | "pull"
  | "close"
  | "confirm-lf"
  | "confirm-of"
  | "edit-settlement"
  | "reject"
  | "edit-client"
  | "route"
  | "reroute";

/** Props mirror the inline component this replaced — `target` carries the
 *  placement id + live decision used for both gating and the placement-side
 *  write; `leadTarget` carries the lead id + status for the lead-side actions
 *  (slice 5c — reject + edit-client). Either may be null (Under Review Misc has
 *  only leadTarget; terminal books may have neither and the panel skips
 *  rendering the footer entirely). The handling-firm name personalizes the
 *  confirm copy; `onClose` collapses the panel on a successful write;
 *  `testId` keys the panel's existing testids. */
export interface CaseDetailActionFooterProps {
  target: CaseDetailActionTarget | null;
  leadTarget: CaseDetailLeadActionTarget | null;
  handlingFirmName: string | null;
  onClose: () => void;
  testId: string;
}

/** Edit-client form values — strings for text/date inputs, boolean for
 *  signed. Initialized from the lazy-fetched live lead. */
type EditClientFormState = {
  name: string;
  phone: string;
  phone2: string;
  email: string;
  caseSummary: string;
  caseType: string;
  incidentDate: string;
  incidentState: string;
  residentState: string;
  address: string;
  internalNotes: string;
  signed: boolean;
};

function leadToFormState(lead: Lead): EditClientFormState {
  return {
    name: lead.name,
    phone: lead.phone ?? "",
    phone2: lead.phone2 ?? "",
    email: lead.email ?? "",
    caseSummary: lead.caseSummary ?? "",
    caseType: lead.caseType ?? "",
    incidentDate: lead.incidentDate ?? "",
    incidentState: lead.incidentState ?? "",
    residentState: lead.residentState ?? "",
    address: lead.address ?? "",
    internalNotes: lead.internalNotes ?? "",
    signed: lead.signed,
  };
}

/** Build the edit-client wire body — only entries that DIFFER from current.
 *  Mirrors slice 5b's editSettlement client-side diff filter. The route's
 *  no-op short-circuit catches the all-equal case; the client-side filter
 *  narrows the wire body for clarity, not correctness. */
function diffEditClient(
  current: Lead,
  form: EditClientFormState,
): Partial<EditClientFormState> {
  const diff: Partial<EditClientFormState> = {};
  // Treat empty form value as "clear the field" — explicit null on the
  // wire. The schema accepts optional, so undefined = don't touch, ""
  // (after null-coalesce) = set to empty string. Keeping the comparison
  // against the empty-string form value handles both: equal-current →
  // omit; differs → include with the form's string value.
  if (form.name !== current.name && form.name.trim() !== "") diff.name = form.name;
  if (form.phone !== (current.phone ?? "")) diff.phone = form.phone;
  if (form.phone2 !== (current.phone2 ?? "")) diff.phone2 = form.phone2;
  if (form.email !== (current.email ?? "")) diff.email = form.email;
  if (form.caseSummary !== (current.caseSummary ?? ""))
    diff.caseSummary = form.caseSummary;
  if (form.caseType !== (current.caseType ?? "")) diff.caseType = form.caseType;
  if (form.incidentDate !== (current.incidentDate ?? ""))
    diff.incidentDate = form.incidentDate;
  if (form.incidentState !== (current.incidentState ?? ""))
    diff.incidentState = form.incidentState;
  if (form.residentState !== (current.residentState ?? ""))
    diff.residentState = form.residentState;
  if (form.address !== (current.address ?? "")) diff.address = form.address;
  if (form.internalNotes !== (current.internalNotes ?? ""))
    diff.internalNotes = form.internalNotes;
  if (form.signed !== current.signed) diff.signed = form.signed;
  return diff;
}

export function CaseDetailActionFooter({
  target,
  leadTarget,
  handlingFirmName,
  onClose,
  testId,
}: CaseDetailActionFooterProps) {
  const decision = target?.decision ?? null;
  const showAccept =
    decision !== null && (ACCEPT_GATE as readonly string[]).includes(decision);
  const showDecline =
    decision !== null && (DECLINE_GATE as readonly string[]).includes(decision);
  const showPull =
    decision !== null && (PULL_GATE as readonly string[]).includes(decision);
  const showClose = canClose(decision);
  // Slice 5b — settlement-band actions. Match the pre-5c gate semantics:
  // `undefined` (the field wasn't projected by this book's mapper) hides
  // the button; only an explicit `null` from the mapper exposes it (Settled
  // book projects payment fields as null until set; Placed book omits
  // them entirely so the buttons stay hidden there).
  const showConfirmLfPayment =
    canConfirmLfPayment(decision) &&
    target !== null &&
    target.lfPaymentDate === null;
  const showConfirmOfPayment =
    canConfirmOfPayment(decision) &&
    target !== null &&
    target.lfPaymentDate != null &&
    target.ofPaymentDate === null;
  const showEditSettlement = canEditSettlement(decision);
  // Slice 5c — lead-level actions. `canReject` is false for terminal lead
  // statuses + null; Edit Client is always available when a lead is in
  // scope (admin-can-always-edit on a non-deleted lead).
  const showReject = leadTarget !== null && canReject(leadTarget.leadStatus);
  const showEditClient = leadTarget !== null;
  // Slice 5d — manual route / reroute, signed transfers only. Route surfaces
  // on a Pending + signed lead (Under Review Misc); Reroute surfaces on a
  // Declined/Pulled placement whose lead is signed (Under Review lead — the
  // mapper projects `linkedLeadSigned`). Both gate the firm-picker modal.
  const showRoute =
    leadTarget !== null &&
    canRoute(leadTarget.leadStatus) &&
    leadTarget.signed === true;
  const showReroute =
    target !== null &&
    canReroute(target.decision) &&
    target.linkedLeadSigned === true;

  const [mode, setMode] = useState<FooterMode>("buttons");
  const [reason, setReason] = useState("");
  // Slice 5c — reject reason. Distinct state from the decline reason so an
  // operator switching between modes doesn't carry text across actions.
  const [rejectReason, setRejectReason] = useState("");
  // Slice F-018 — optional close reason (ADR-0075). Submittable empty; a blank
  // value posts a bodyless close.
  const [closeReason, setCloseReason] = useState("");

  // Edit-settlement form state — prefilled from the live actionTarget.
  const [amountInput, setAmountInput] = useState<string>(
    target?.settlementAmount != null ? String(target.settlementAmount) : "",
  );
  const [rateInput, setRateInput] = useState<string>(
    target?.attorneyFeeRate != null ? String(target.attorneyFeeRate) : "",
  );
  const [dateInput, setDateInput] = useState<string>(
    target?.settlementDate != null ? target.settlementDate : "",
  );

  // Slice 5c — edit-client form state. Lazy-loaded on form open via fetchLead
  // (avoids piping 13 fields through every book composer). Three reads of
  // the state machine: not-loaded (initial), loading (fetch in flight),
  // loaded (form prefilled). `liveLead` is the snapshot the diff submit
  // compares against on save.
  const [liveLead, setLiveLead] = useState<Lead | null>(null);
  const [loadingLead, setLoadingLead] = useState(false);
  const [loadLeadError, setLoadLeadError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditClientFormState | null>(null);

  const {
    accept,
    decline,
    pull,
    closeCase,
    confirmLfPayment,
    confirmOfPayment,
    editSettlement,
    reject,
    editClient,
    fetchLead,
    route,
    reroute,
    fetchEligibleFirmsForRoute,
    fetchEligibleFirmsForReroute,
  } = usePlacementAction();
  const pending =
    accept.isPending ||
    decline.isPending ||
    pull.isPending ||
    closeCase.isPending ||
    confirmLfPayment.isPending ||
    confirmOfPayment.isPending ||
    editSettlement.isPending ||
    reject.isPending ||
    editClient.isPending ||
    route.isPending ||
    reroute.isPending;

  // Slice 5d — firm-picker state for route / reroute. Lazy: only fetches when
  // the mode opens. `eligibilityOverride` is the operator's escape hatch for
  // hand-cleared edge cases (mirrors portal). `pickerFirms === null` means
  // not-yet-loaded; an empty array means "loaded, zero candidates."
  const [pickerFirms, setPickerFirms] = useState<EligibleFirm[] | null>(null);
  const [loadingPicker, setLoadingPicker] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [eligibilityOverride, setEligibilityOverride] = useState(false);

  // Fire the lead fetch when the edit-client form opens. Effect-driven so
  // it doesn't fire if the operator never clicks Edit Client.
  useEffect(() => {
    if (
      mode !== "edit-client" ||
      leadTarget === null ||
      liveLead !== null ||
      loadingLead ||
      // A prior fetch errored — don't silently retry on every re-render
      // (F-010). The operator reopens the form to retry.
      loadLeadError !== null
    ) {
      return;
    }
    setLoadingLead(true);
    setLoadLeadError(null);
    fetchLead(leadTarget.leadId).then(
      (lead) => {
        setLiveLead(lead);
        setEditForm(leadToFormState(lead));
        setLoadingLead(false);
      },
      (err: Error) => {
        setLoadLeadError(err.message ?? "Could not load the lead.");
        setLoadingLead(false);
      },
    );
  }, [mode, leadTarget, liveLead, loadingLead, loadLeadError, fetchLead]);

  // Slice 5d — fire the eligibility fetch when the picker opens. Two
  // entrypoints (route on a lead id, reroute on a declined placement id);
  // the response shape is identical so one piece of state covers both.
  useEffect(() => {
    if (mode !== "route" && mode !== "reroute") return;
    if (pickerFirms !== null || loadingPicker) return;
    // A prior fetch errored — don't silently retry every render (F-010).
    if (pickerError !== null) return;
    if (mode === "route" && leadTarget === null) return;
    if (mode === "reroute" && target === null) return;
    setLoadingPicker(true);
    setPickerError(null);
    const fetchPromise =
      mode === "route"
        ? fetchEligibleFirmsForRoute(leadTarget!.leadId)
        : fetchEligibleFirmsForReroute(target!.placementId);
    fetchPromise.then(
      (response) => {
        setPickerFirms(response.firms);
        setLoadingPicker(false);
      },
      (err: Error) => {
        setPickerError(err.message ?? "Could not load eligible firms.");
        setLoadingPicker(false);
      },
    );
  }, [
    mode,
    leadTarget,
    target,
    pickerFirms,
    loadingPicker,
    pickerError,
    fetchEligibleFirmsForRoute,
    fetchEligibleFirmsForReroute,
  ]);

  // A terminal / out-of-band decision exposes no action AND no lead-side
  // action either — the panel is read-only for that matter.
  if (
    !showAccept &&
    !showDecline &&
    !showPull &&
    !showClose &&
    !showConfirmLfPayment &&
    !showConfirmOfPayment &&
    !showEditSettlement &&
    !showReject &&
    !showEditClient &&
    !showRoute &&
    !showReroute
  ) {
    return null;
  }

  /** Slice 5d — close the picker mode + reset its state. The override flag
   *  resets too: a fresh open shouldn't carry the prior session's choice. */
  function closePicker(): void {
    setMode("buttons");
    setPickerFirms(null);
    setLoadingPicker(false);
    setPickerError(null);
    setEligibilityOverride(false);
  }

  // Edit-settlement submit — only entries that DIFFER from current.
  function submitEditSettlement(): void {
    if (target === null) return;
    const fields: {
      settlementAmount?: number;
      attorneyFeeRate?: number;
      settlementDate?: string;
    } = {};
    if (amountInput.trim() !== "") {
      const amount = Number(amountInput);
      if (Number.isFinite(amount) && amount !== target.settlementAmount) {
        fields.settlementAmount = amount;
      }
    }
    if (rateInput.trim() !== "") {
      const rate = Number(rateInput);
      if (Number.isFinite(rate) && rate !== target.attorneyFeeRate) {
        fields.attorneyFeeRate = rate;
      }
    }
    if (dateInput.trim() !== "" && dateInput !== target.settlementDate) {
      fields.settlementDate = dateInput;
    }
    if (
      fields.settlementAmount === undefined &&
      fields.attorneyFeeRate === undefined &&
      fields.settlementDate === undefined
    ) {
      setMode("buttons");
      return;
    }
    editSettlement.mutate(
      { placementId: target.placementId, ...fields },
      { onSuccess: onClose },
    );
  }

  // Edit-client submit — diff the form against the live lead. If nothing
  // changed, collapse without a request (mirrors the settlement form).
  function submitEditClient(): void {
    if (leadTarget === null || liveLead === null || editForm === null) return;
    const diff = diffEditClient(liveLead, editForm);
    if (Object.keys(diff).length === 0) {
      setMode("buttons");
      return;
    }
    editClient.mutate(
      { leadId: leadTarget.leadId, ...diff },
      { onSuccess: onClose },
    );
  }

  return (
    <footer className="border-t bg-card text-card-foreground px-5 py-4" data-testid={`${testId}-actions`}>
      {handlingFirmName ? (
        <p className="mb-2 text-xs text-muted-foreground">
          Acting on behalf of {handlingFirmName}.
        </p>
      ) : null}
      {mode === "decline" && target ? (
        <div className="flex flex-col gap-2">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is the firm declining? (recorded on the placement)"
            rows={3}
            autoFocus
            data-testid={`${testId}-decline-reason`}
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              disabled={pending || reason.trim() === ""}
              onClick={() =>
                decline.mutate(
                  { placementId: target.placementId, reason: reason.trim() },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-decline-confirm`}
            >
              {decline.isPending ? "Declining…" : "Confirm decline"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => {
                setMode("buttons");
                setReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "pull" && target ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-foreground">
            Pull {handlingFirmName ?? "this firm"}? The matter will reroute to
            another firm, reject, or return to Pending.
          </p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              disabled={pending}
              onClick={() =>
                pull.mutate(
                  { placementId: target.placementId },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-pull-confirm`}
            >
              {pull.isPending ? "Pulling…" : "Confirm pull"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("buttons")}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "close" && target ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-foreground">
            Close this case? Stamps today as the Case Closed Date — the matter
            moves to the Closed book and post-placement work ends.
          </p>
          <Textarea
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            placeholder="Reason for closing (optional — recorded in the audit log)"
            rows={2}
            data-testid={`${testId}-close-reason`}
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              disabled={pending}
              onClick={() =>
                closeCase.mutate(
                  {
                    placementId: target.placementId,
                    // Optional: send the key only when non-blank.
                    ...(closeReason.trim() !== ""
                      ? { close_reason: closeReason.trim() }
                      : {}),
                  },
                  {
                    onSuccess: () => {
                      setCloseReason("");
                      onClose();
                    },
                  },
                )
              }
              data-testid={`${testId}-close-case-confirm`}
            >
              {closeCase.isPending ? "Closing…" : "Confirm close"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => {
                setMode("buttons");
                setCloseReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "confirm-lf" && target ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-foreground">
            Confirm LegalFlare received its disbursement share?
            Stamps today as the LF Payment Date and updates Last Update.
          </p>
          <div className="flex gap-2">
            <Button
              disabled={pending}
              onClick={() =>
                confirmLfPayment.mutate(
                  { placementId: target.placementId },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-confirm-lf-payment-confirm`}
            >
              {confirmLfPayment.isPending ? "Confirming…" : "Confirm LF received"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("buttons")}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "confirm-of" && target ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-foreground">
            Confirm the Originating Firm received its share? Stamps today as the
            OF Payment Date.
          </p>
          <div className="flex gap-2">
            <Button
              disabled={pending}
              onClick={() =>
                confirmOfPayment.mutate(
                  { placementId: target.placementId },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-confirm-of-payment-confirm`}
            >
              {confirmOfPayment.isPending ? "Confirming…" : "Confirm OF received"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("buttons")}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "edit-settlement" && target ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            Amend the HF-submitted settlement figures. Leave a field unchanged
            to keep its current value.
          </p>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">
              Settlement amount ($)
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="rounded border border-input bg-background px-2 py-1 text-sm"
              data-testid={`${testId}-edit-settlement-amount`}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">
              Attorney fee rate (decimal, max {ATTORNEY_FEE_RATE_MAX})
            </span>
            <input
              type="number"
              min="0"
              max={ATTORNEY_FEE_RATE_MAX}
              step="0.01"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              className="rounded border border-input bg-background px-2 py-1 text-sm"
              data-testid={`${testId}-edit-settlement-rate`}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">
              Settlement date (YYYY-MM-DD)
            </span>
            <input
              type="date"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              className="rounded border border-input bg-background px-2 py-1 text-sm"
              data-testid={`${testId}-edit-settlement-date`}
            />
          </label>
          <div className="flex gap-2">
            <Button
              disabled={pending}
              onClick={submitEditSettlement}
              data-testid={`${testId}-edit-settlement-confirm`}
            >
              {editSettlement.isPending ? "Saving…" : "Save changes"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => {
                // Reset the inputs to the live target before closing, so a
                // type → cancel → reopen shows current values (matches the
                // Decline / Reject / Edit-Client cancel behaviour).
                setAmountInput(
                  target.settlementAmount != null
                    ? String(target.settlementAmount)
                    : "",
                );
                setRateInput(
                  target.attorneyFeeRate != null
                    ? String(target.attorneyFeeRate)
                    : "",
                );
                setDateInput(
                  target.settlementDate != null ? target.settlementDate : "",
                );
                setMode("buttons");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "reject" && leadTarget ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            Reject this lead — marks the lead Rejected and auto-declines every
            non-terminal placement on it so workflows stop chasing. The cascade
            is strand-free (Decision Date null-out is inherited from
            placement-decline; see ADR-0064).
          </p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why is this lead being rejected? (recorded on the lead + propagated to cascaded placements)"
            rows={3}
            autoFocus
            data-testid={`${testId}-reject-lead-reason`}
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              disabled={pending || rejectReason.trim() === ""}
              onClick={() =>
                reject.mutate(
                  {
                    leadId: leadTarget.leadId,
                    reason: rejectReason.trim(),
                  },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-reject-lead-confirm`}
            >
              {reject.isPending ? "Rejecting…" : "Confirm reject"}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => {
                setMode("buttons");
                setRejectReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : mode === "edit-client" && leadTarget ? (
        <div className="flex flex-col gap-2">
          {loadingLead ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid={`${testId}-edit-client-loading`}
            >
              Loading lead…
            </p>
          ) : loadLeadError !== null ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-error">{loadLeadError}</p>
              <Button
                variant="outline"
                onClick={() => {
                  setMode("buttons");
                  setLoadLeadError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : editForm !== null && liveLead !== null ? (
            <>
              <p className="text-xs text-muted-foreground">
                Edit client information. Leave a field unchanged to keep its
                current value.
              </p>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">Name</span>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  className="rounded border border-input bg-background px-2 py-1 text-sm"
                  data-testid={`${testId}-edit-client-name`}
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">Phone</span>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-phone`}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">Phone 2</span>
                  <input
                    type="tel"
                    value={editForm.phone2}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone2: e.target.value })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-phone2`}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">Email</span>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  className="rounded border border-input bg-background px-2 py-1 text-sm"
                  data-testid={`${testId}-edit-client-email`}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">Address</span>
                <Textarea
                  value={editForm.address}
                  onChange={(e) =>
                    setEditForm({ ...editForm, address: e.target.value })
                  }
                  rows={2}
                  data-testid={`${testId}-edit-client-address`}
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">
                    Incident state
                  </span>
                  <select
                    value={editForm.incidentState}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        incidentState: e.target.value,
                      })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-incident-state`}
                  >
                    <option value="">—</option>
                    {US_STATE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">
                    Resident state
                  </span>
                  <select
                    value={editForm.residentState}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        residentState: e.target.value,
                      })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-resident-state`}
                  >
                    <option value="">—</option>
                    {US_STATE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">
                    Case type
                  </span>
                  <select
                    value={editForm.caseType}
                    onChange={(e) =>
                      setEditForm({ ...editForm, caseType: e.target.value })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-case-type`}
                  >
                    <option value="">—</option>
                    {LEAD_CASE_TYPES.map((ct) => (
                      <option key={ct} value={ct}>
                        {ct}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted-foreground">
                    Incident date (YYYY-MM-DD)
                  </span>
                  <input
                    type="date"
                    value={editForm.incidentDate}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        incidentDate: e.target.value,
                      })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-sm"
                    data-testid={`${testId}-edit-client-incident-date`}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">
                  Case summary
                </span>
                <Textarea
                  value={editForm.caseSummary}
                  onChange={(e) =>
                    setEditForm({ ...editForm, caseSummary: e.target.value })
                  }
                  rows={3}
                  data-testid={`${testId}-edit-client-case-summary`}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">
                  Internal notes
                </span>
                <Textarea
                  value={editForm.internalNotes}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      internalNotes: e.target.value,
                    })
                  }
                  rows={2}
                  data-testid={`${testId}-edit-client-internal-notes`}
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editForm.signed}
                  onChange={(e) =>
                    setEditForm({ ...editForm, signed: e.target.checked })
                  }
                  data-testid={`${testId}-edit-client-signed`}
                />
                <span>Transfer (signed referral)</span>
              </label>
              <div className="flex gap-2">
                <Button
                  disabled={pending}
                  onClick={submitEditClient}
                  data-testid={`${testId}-edit-client-save`}
                >
                  {editClient.isPending ? "Saving…" : "Save changes"}
                </Button>
                <Button
                  variant="outline"
                  disabled={pending}
                  onClick={() => {
                    setMode("buttons");
                    // Clear cached lead so a re-open re-fetches fresh.
                    setLiveLead(null);
                    setEditForm(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : (mode === "route" && leadTarget) || (mode === "reroute" && target) ? (
        <FirmPickerForm
          mode={mode as "route" | "reroute"}
          title={
            mode === "route"
              ? `Route ${leadTarget?.leadHumanId ?? "lead"} to a Handling Firm`
              : `Reroute ${target?.placementId ?? "placement"}`
          }
          firms={pickerFirms}
          loading={loadingPicker}
          error={pickerError}
          eligibilityOverride={eligibilityOverride}
          onEligibilityOverrideChange={setEligibilityOverride}
          submitting={route.isPending || reroute.isPending}
          onPick={(firmId) => {
            if (mode === "route" && leadTarget) {
              route.mutate(
                {
                  leadId: leadTarget.leadId,
                  firmId,
                  ...(eligibilityOverride ? { eligibilityOverride: true } : {}),
                },
                { onSuccess: onClose },
              );
            } else if (mode === "reroute" && target) {
              reroute.mutate(
                {
                  declinedPlacementId: target.placementId,
                  firmId,
                  ...(eligibilityOverride ? { eligibilityOverride: true } : {}),
                },
                { onSuccess: onClose },
              );
            }
          }}
          onCancel={closePicker}
          testId={testId}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {showAccept && target ? (
            <Button
              disabled={pending}
              onClick={() =>
                accept.mutate(
                  { placementId: target.placementId },
                  { onSuccess: onClose },
                )
              }
              data-testid={`${testId}-accept`}
            >
              {accept.isPending ? "Accepting…" : "Accept"}
            </Button>
          ) : null}
          {showDecline ? (
            <Button
              variant="destructive"
              disabled={pending}
              onClick={() => setMode("decline")}
              data-testid={`${testId}-decline`}
            >
              Decline
            </Button>
          ) : null}
          {showPull ? (
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("pull")}
              data-testid={`${testId}-pull`}
            >
              Pull
            </Button>
          ) : null}
          {showClose ? (
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("close")}
              data-testid={`${testId}-close-case`}
            >
              Close Case
            </Button>
          ) : null}
          {showConfirmLfPayment ? (
            <Button
              disabled={pending}
              onClick={() => setMode("confirm-lf")}
              data-testid={`${testId}-confirm-lf-payment`}
            >
              Confirm LF Payment
            </Button>
          ) : null}
          {showConfirmOfPayment ? (
            <Button
              disabled={pending}
              onClick={() => setMode("confirm-of")}
              data-testid={`${testId}-confirm-of-payment`}
            >
              Confirm OF Payment
            </Button>
          ) : null}
          {showEditSettlement ? (
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("edit-settlement")}
              data-testid={`${testId}-edit-settlement`}
            >
              Edit Settlement
            </Button>
          ) : null}
          {showReject ? (
            <Button
              variant="destructive"
              disabled={pending}
              onClick={() => setMode("reject")}
              data-testid={`${testId}-reject-lead`}
            >
              Reject Lead
            </Button>
          ) : null}
          {showEditClient ? (
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setMode("edit-client")}
              data-testid={`${testId}-edit-client`}
            >
              Edit Client
            </Button>
          ) : null}
          {showRoute ? (
            <Button
              disabled={pending}
              onClick={() => setMode("route")}
              data-testid={`${testId}-route`}
            >
              Route
            </Button>
          ) : null}
          {showReroute ? (
            <Button
              disabled={pending}
              onClick={() => setMode("reroute")}
              data-testid={`${testId}-reroute`}
            >
              Reroute
            </Button>
          ) : null}
        </div>
      )}

      {accept.isError ||
      decline.isError ||
      pull.isError ||
      closeCase.isError ||
      confirmLfPayment.isError ||
      confirmOfPayment.isError ||
      editSettlement.isError ||
      reject.isError ||
      editClient.isError ||
      route.isError ||
      reroute.isError ? (
        <p
          className="mt-2 text-xs text-error"
          data-testid={`${testId}-action-error`}
        >
          Couldn't save that action — the matter may have already changed.
          Refresh and retry.
        </p>
      ) : null}
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Slice 5d — FirmPickerForm
// ---------------------------------------------------------------------------

/** Props for the inline firm-picker form. Same shape powers Route and Reroute;
 *  `mode` distinguishes the title + the action the parent fires on pick. */
interface FirmPickerFormProps {
  mode: "route" | "reroute";
  title: string;
  firms: EligibleFirm[] | null;
  loading: boolean;
  error: string | null;
  eligibilityOverride: boolean;
  onEligibilityOverrideChange: (override: boolean) => void;
  submitting: boolean;
  onPick: (firmId: string) => void;
  onCancel: () => void;
  testId: string;
}

/**
 * Vertical list of eligible Handling Firms. Each row:
 *   - Firm name + designation chip
 *   - States covered (compact summary)
 *   - "Previously declined" warning chip when applicable (date + truncated
 *     reason), so the operator doesn't unwittingly re-pick a firm that
 *     already said no
 *   - Pick button on the right
 *
 * Below the list: a small "ignore eligibility" toggle (LegalFlare staff's judgment
 * lever) and a Cancel. The toggle does NOT trigger a re-fetch — the picker
 * always shows the eligibility-filtered list. The toggle just adds the
 * override flag to the body so the SERVER's eligibility gate is bypassed
 * on POST.
 *
 * **Scope cut.** When the filter returns zero firms, there is no way to
 * pick an outside-eligibility firm from this picker. Override flips the
 * server's gate only on candidates the picker already lists; if the list
 * is empty there is nothing to override-pick. The workarounds are: (1) fix
 * the lead's case-type / state via Edit Client so eligibility opens up,
 * or (2) use the legacy portal admin UI (still live) for true
 * outside-eligibility routes. A future "Show all firms (override
 * required)" affordance — a second fetch returning every Active Handling
 * firm — would close this gap; deferred.
 */
function FirmPickerForm({
  title,
  firms,
  loading,
  error,
  eligibilityOverride,
  onEligibilityOverrideChange,
  submitting,
  onPick,
  onCancel,
  testId,
}: FirmPickerFormProps) {
  return (
    <div className="flex flex-col gap-3" data-testid={`${testId}-picker`}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading eligible firms…</p>
      ) : error !== null ? (
        <p
          className="text-sm text-error"
          data-testid={`${testId}-picker-error`}
        >
          {error}
        </p>
      ) : firms !== null && firms.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No eligible firms match this lead. Fix the lead's case-type / state
          via Edit Client to open eligibility, or use the legacy portal admin
          UI for an outside-eligibility route.
        </p>
      ) : firms !== null ? (
        <ul className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
          {firms.map((firm) => (
            <li
              key={firm.id}
              className="flex items-start justify-between gap-3 rounded border border-border bg-card text-card-foreground px-3 py-2"
              data-testid={`${testId}-picker-firm-${firm.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {firm.name}
                </p>
                {firm.states.length > 0 ? (
                  <p className="text-xs text-muted-foreground truncate">
                    {firm.states.slice(0, 6).join(", ")}
                    {firm.states.length > 6 ? "…" : ""}
                  </p>
                ) : null}
                {firm.previouslyDeclined !== null ? (
                  <p
                    className="text-xs text-warning mt-1"
                    data-testid={`${testId}-picker-firm-${firm.id}-declined`}
                  >
                    Previously declined
                    {firm.previouslyDeclined.date
                      ? ` ${firm.previouslyDeclined.date.slice(0, 10)}`
                      : ""}
                    {firm.previouslyDeclined.reason
                      ? `: ${firm.previouslyDeclined.reason.slice(0, 100)}`
                      : ""}
                  </p>
                ) : null}
              </div>
              <Button
                size="sm"
                disabled={submitting}
                onClick={() => onPick(firm.id)}
                data-testid={`${testId}-picker-firm-${firm.id}-pick`}
              >
                Pick
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={eligibilityOverride}
          onChange={(e) => onEligibilityOverrideChange(e.target.checked)}
          data-testid={`${testId}-picker-override`}
        />
        <span>
          Ignore eligibility (pick a firm outside the lead's case type / state)
        </span>
      </label>
      <div>
        <Button
          variant="outline"
          disabled={submitting}
          onClick={onCancel}
          data-testid={`${testId}-picker-cancel`}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
